import { Save } from 'lucide-react';

import ColorInput from 'components/storefront/StorefrontConfigEditor/components/ColorInput';
import ImageInput from 'components/storefront/StorefrontConfigEditor/components/ImageInput';
import { Button } from 'shadcn/components/ui/Button';
import { Checkbox } from 'shadcn/components/ui/Checkbox';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { Textarea } from 'shadcn/components/ui/Textarea';

/**
 * Form for the banner editor.
 *
 * @param {number} aisleID - Aisle ID.
 * @param {Object} data - Non-configurable banner data.
 * @param {Object} formData - Configurable banner data.
 * @param {Function} handleChange - Callback function to handle input changes.
 * @param {Function} handleImageClear - Callback function to handle image clear.
 * @param {Function} handleImageUpload - Callback function to handle image upload.
 * @param {Function} handleSave - Callback function to handle form submission.
 * @param {Object} imageErrors - Image errors.
 * @param {boolean} isSaving - Whether the form is being saved.
 */
export default function BannerEditorForm({
    aisleID,
    data,
    formData,
    handleChange,
    handleImageClear,
    handleImageUpload,
    handleSave,
    imageErrors,
    isSaving,
}) {
    return (
        <form className="flex flex-col gap-6" onSubmit={handleSave}>
            <div className="col-span-2 flex items-center gap-2">
                <Checkbox
                    id={`active-${data.id}`}
                    name="active"
                    checked={formData.active}
                    onCheckedChange={(checked) => handleChange({ target: { name: 'active', value: checked } })}
                    className="h-5 w-5"
                />
                <Label className="font-normal cursor-pointer" htmlFor={`active-${data.id}`}>
                    Active
                </Label>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <ImageInput
                    className="[&_img]:max-h-20"
                    id={`banner-${data.id}-large-image`}
                    imageUrl={formData.largeImage}
                    label="Large Image"
                    onClearImage={handleImageClear}
                    onImageUpload={(file) => handleImageUpload(file, 'largeImage')}
                    error={imageErrors.largeImage}
                    required
                />

                <ImageInput
                    className="[&_img]:max-h-20"
                    id={`banner-${data.id}-small-image`}
                    imageUrl={formData.smallImage}
                    label="Small Image"
                    onClearImage={handleImageClear}
                    onImageUpload={(file) => handleImageUpload(file, 'smallImage')}
                    error={imageErrors.smallImage}
                    required
                />
            </div>

            <div className="flex flex-col gap-2">
                <Label htmlFor={`banner-name-${aisleID}`}>Name</Label>
                <Input
                    type="text"
                    id={`banner-name-${aisleID}`}
                    name="bannerName"
                    value={formData.bannerName}
                    onChange={handleChange}
                    placeholder="Enter name"
                    required
                />
            </div>

            <div className="flex flex-col gap-2">
                <Label htmlFor={`banner-copy-${data.id}`}>Copy</Label>
                <Textarea
                    type="text"
                    id={`banner-copy-${data.id}`}
                    name="copy"
                    value={formData.copy}
                    onChange={handleChange}
                    placeholder="Enter copy"
                />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                    <Label htmlFor={`link-url-${data.id}`}>Link URL</Label>
                    <Input
                        type="text"
                        id={`link-url-${data.id}`}
                        name="linkURL"
                        value={formData.linkURL}
                        placeholder="Enter link URL"
                        onChange={handleChange}
                    />
                </div>

                <div className="flex flex-col gap-2">
                    <Label htmlFor={`link-title-${data.id}`}>Link Text</Label>
                    <Input
                        type="text"
                        id={`link-title-${data.id}`}
                        name="linkTitle"
                        value={formData.linkTitle}
                        placeholder="Enter link text"
                        onChange={handleChange}
                    />
                </div>
            </div>

            <ColorInput
                id={`banner-bg-color-${data.id}`}
                label="Background Color"
                name="backgroundColor"
                value={formData.backgroundColor}
                onChange={handleChange}
            />

            <Button className="self-end w-content" disabled={isSaving}>
                <Save />
                Save changes
            </Button>
        </form>
    );
}
